import { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCount } from '../../api/queueMonitor';
import { TrackedItem } from '../../hooks/useRequeue';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import './Requeue.scss';

export interface ITrackedSummary {
    /** How many objects we track since they were requeued. */
    total: number;
    /** How many of those we have checked since. */
    checked: number;
    /** How many of those have reached Exact by now. */
    synced: number;
}

export interface ITrackingPanelProps {
    tracked: ITrackedSummary;
    /** The tracked objects themselves, so one of them can be followed from here. */
    items: TrackedItem[];
    polling: boolean;
    checking: boolean;

    onCheck: () => void;
    onStopPolling: () => void;
    onForget: () => void;
    /** Opens the trace of one tracked object. */
    onTrace: (id: string, objectType: string, relationId: string) => void;

    open: boolean;
    onToggle: () => void;
    /**
     * What the last requeue reported, drawn under the list it filled.
     *
     * The result card used to live down in the error log, next to the button that produced
     * it - which put the objects that were just offered again in one section and the list
     * that follows those very objects in another. They are one story, so they are now in
     * one place.
     */
    children?: ReactNode;
}

/**
 * How many tracked objects get a chip of their own.
 *
 * The list is capped at a couple of hundred, and a wall of that many chips is not a list
 * anybody reads. The counter above it already says how many there are in total; these are
 * only the way in to one of them.
 */
const SHOWN_ITEMS = 24;

/**
 * What the tracking list is, in the words of someone seeing it for the first time.
 *
 * This was written down nowhere, and that was exactly the problem: above the table sat a
 * count from this list and next to the heading a count from the error log, and those two
 * cover a different set of objects over a different period. Two unrelated numbers side by
 * side read as one number that is wrong.
 */
const TRACKING_HINT =
    'De volglijst zijn de objecten die vanuit deze browser opnieuw zijn aangeboden, ook die uit eerdere sessies. Hij staat lokaal opgeslagen, dus een collega ziet hem niet. Dit aantal staat los van het aantal regels in het foutenlogboek: dat zijn de fouten in de gekozen periode.';

/** What the button does, stated as what it does rather than what it is called. */
const CHECK_HINT =
    'Vraagt per gevolgd object op of er inmiddels een Exact-id bij staat. Dit leest alleen: er wordt niets opnieuw aangeboden en er verandert niets in Exact.';

const IN_EXACT_HINT =
    'Er staat een echte Exact-id bij dit object, dus het is aangekomen. Hier hoef je niets meer mee.';

const NOT_IN_EXACT_HINT =
    'Opgevraagd, maar er staat nog geen Exact-id bij. Het bericht wacht nog in de queue, of het is opnieuw stukgelopen en staat dan in het foutenlogboek.';

const UNCHECKED_HINT =
    'Van deze objecten is de stand nog niet opgehaald. Dat zegt niets over of ze wel of niet in Exact staan - klik op "Status ophalen".';

const TRACE_HINT =
    'Laat zien wat er met dit object is gebeurd: waar het staat, wat er bij elke poging misging en waar het op wacht.';

/**
 * The objects we keep an eye on until they have reached Exact.
 *
 * A section of its own, and it renders even when nothing is tracked. Inside the result card
 * it came and went with the requeue that produced it, which moved everything underneath it
 * up and down the page - including the row somebody was about to click. Standing still is
 * worth an empty state.
 */
export const TrackingPanel = (props: ITrackingPanelProps) => {
    const { total, checked, synced } = props.tracked;

    /*
        Three states rather than three loose numbers: in Exact, not there yet, and unknown.
        That last one is the state the old wording read straight past, while it is exactly
        the difference between nothing to do and work ahead.
    */
    const pending = Math.max(0, checked - synced);
    const unchecked = Math.max(0, total - checked);
    const share = (value: number) => `${(value / Math.max(1, total)) * 100}%`;

    const headline =
        checked === 0
            ? `Nog niet opgevraagd of deze ${formatCount(
                  total
              )} object(en) in Exact staan.`
            : synced === total
            ? `Alle ${formatCount(total)} object(en) staan in Exact.`
            : `${formatCount(synced)} van ${formatCount(
                  total
              )} staat in Exact.`;

    const meterLabel = `${formatCount(synced)} in Exact, ${formatCount(
        pending
    )} nog niet in Exact, ${formatCount(unchecked)} nog niet opgevraagd.`;

    return (
        <CollapsibleSection
            id="tracking-panel"
            className="tracking-panel"
            title="Volglijst"
            hint={TRACKING_HINT}
            open={props.open}
            onToggle={props.onToggle}
            status={
                <span className="tracking-panel__text">
                    {total === 0 ? (
                        'nog niets gevolgd'
                    ) : (
                        <>
                            {synced === total && (
                                <FontAwesomeIcon
                                    icon="check"
                                    className="me-2 text-success"
                                />
                            )}
                            {headline}
                        </>
                    )}
                </span>
            }
            tools={
                total > 0 ? (
                    <>
                        <button
                            type="button"
                            className="requeue-link"
                            disabled={props.checking}
                            onClick={props.onCheck}
                            title={CHECK_HINT}
                        >
                            {props.checking && (
                                <FontAwesomeIcon
                                    icon="spinner"
                                    spin
                                    className="me-2"
                                />
                            )}
                            Status ophalen
                        </button>

                        {props.polling && (
                            <button
                                type="button"
                                className="requeue-link"
                                onClick={props.onStopPolling}
                                title="Stopt met elke vijf seconden opnieuw opvragen. De volglijst zelf blijft staan."
                            >
                                Stop met volgen
                            </button>
                        )}

                        <button
                            type="button"
                            className="requeue-link requeue-link--muted"
                            onClick={props.onForget}
                            title="Wis de lijst met gevolgde objecten; de berichten zelf blijven gewoon in de queue staan"
                        >
                            Volglijst wissen
                        </button>
                    </>
                ) : undefined
            }
        >
            <div className="tracking-panel__body">
                {total === 0 ? (
                    <p className="tracking-panel__empty">
                        Nog niets gevolgd. Wat je opnieuw aanbiedt komt hier te
                        staan, tot het in Exact staat.
                    </p>
                ) : (
                    <>
                        <div
                            className="tracking-panel__meter"
                            role="img"
                            aria-label={meterLabel}
                        >
                            {synced > 0 && (
                                <span
                                    className="tracking-panel__meter-part tracking-panel__meter-part--ok"
                                    style={{ width: share(synced) }}
                                    title={IN_EXACT_HINT}
                                />
                            )}

                            {pending > 0 && (
                                <span
                                    className="tracking-panel__meter-part tracking-panel__meter-part--no"
                                    style={{ width: share(pending) }}
                                    title={NOT_IN_EXACT_HINT}
                                />
                            )}

                            {unchecked > 0 && (
                                <span
                                    className="tracking-panel__meter-part tracking-panel__meter-part--unknown"
                                    style={{ width: share(unchecked) }}
                                    title={UNCHECKED_HINT}
                                />
                            )}
                        </div>

                        <ul className="tracking-panel__legend">
                            <li
                                className="tracking-panel__legend-item tracking-panel__legend-item--ok"
                                title={IN_EXACT_HINT}
                            >
                                {formatCount(synced)} in Exact
                            </li>

                            {pending > 0 && (
                                <li
                                    className="tracking-panel__legend-item tracking-panel__legend-item--no"
                                    title={NOT_IN_EXACT_HINT}
                                >
                                    {formatCount(pending)} nog niet in Exact
                                </li>
                            )}

                            {unchecked > 0 && (
                                <li
                                    className="tracking-panel__legend-item tracking-panel__legend-item--unknown"
                                    title={UNCHECKED_HINT}
                                >
                                    {formatCount(unchecked)} nog niet opgevraagd
                                </li>
                            )}

                            {props.polling && (
                                <li className="tracking-panel__legend-item tracking-panel__legend-item--busy">
                                    <FontAwesomeIcon
                                        icon="spinner"
                                        spin
                                        className="me-2"
                                    />
                                    wordt elke 5 sec bijgewerkt
                                </li>
                            )}
                        </ul>

                        {/*
                            The way from a number to a thing. The meter says how many are
                            still stuck; this says which ones, and one click says why.
                        */}
                        <ul className="tracking-panel__items">
                            {props.items.slice(0, SHOWN_ITEMS).map((item) => (
                                <li
                                    key={`${item.objectType}-${item.relationId}`}
                                >
                                    <button
                                        type="button"
                                        className="tracking-panel__item"
                                        title={TRACE_HINT}
                                        onClick={() =>
                                            props.onTrace(
                                                item.relationId,
                                                item.objectType,
                                                item.relationId
                                            )
                                        }
                                    >
                                        <span className="tracking-panel__item-type">
                                            {item.objectType}
                                        </span>
                                        {item.relationId}
                                    </button>
                                </li>
                            ))}

                            {props.items.length > SHOWN_ITEMS && (
                                <li className="tracking-panel__item-more">
                                    en nog{' '}
                                    {formatCount(
                                        props.items.length - SHOWN_ITEMS
                                    )}
                                </li>
                            )}
                        </ul>
                    </>
                )}

                {props.children}
            </div>
        </CollapsibleSection>
    );
};
